import React, { createContext, useContext, useCallback, useMemo } from 'react';
import { CalendarEvent } from '../hooks/useSupabaseEvents';
import { Calendar } from '../types';
import { Todo } from '../hooks/useSupabaseKanban';

interface CalendarContextType {
  calendars: Calendar[];
  onTodoToggle: (todoId: string) => void;
  onTodoDrag: (todoId: string, dueDate: string, dueTime: string | null) => Promise<void>;
  quickUpdateEvent: (eventId: string | number, updates: Partial<CalendarEvent>) => Promise<void>;
  deleteMultipleEvents: (eventIds: (string | number)[]) => Promise<void>;
}

const CalendarContext = createContext<CalendarContextType | null>(null);

interface CalendarProviderProps {
  children: React.ReactNode;
  calendars: Calendar[];
  todos: Todo[];
  kanbanColumns: { id: string }[];
  toggleTodoComplete: (todoId: string) => void;
  updateTodoDueDate: (todoId: string, dueDate: string, dueTime: string | null) => Promise<void>;
  quickUpdateEvent: (eventId: string | number, updates: Partial<CalendarEvent>) => Promise<void>;
  deleteMultipleEvents: (eventIds: (string | number)[]) => Promise<void>;
}

export function CalendarProvider({
  children,
  calendars,
  todos: _todos,
  kanbanColumns: _kanbanColumns,
  toggleTodoComplete,
  updateTodoDueDate,
  quickUpdateEvent,
  deleteMultipleEvents,
}: CalendarProviderProps) {
  const onTodoToggle = useCallback((todoId: string) => {
    toggleTodoComplete(todoId);
  }, [toggleTodoComplete]);

  const onTodoDrag = useCallback(async (todoId: string, dueDate: string, dueTime: string | null) => {
    await updateTodoDueDate(todoId, dueDate, dueTime);
  }, [updateTodoDueDate]);

  const value = useMemo(() => ({
    calendars,
    onTodoToggle,
    onTodoDrag,
    quickUpdateEvent,
    deleteMultipleEvents,
  }), [calendars, onTodoToggle, onTodoDrag, quickUpdateEvent, deleteMultipleEvents]);

  return (
    <CalendarContext.Provider value={value}>
      {children}
    </CalendarContext.Provider>
  );
}

export function useCalendar() {
  const context = useContext(CalendarContext);
  if (!context) {
    throw new Error('useCalendar must be used within a CalendarProvider');
  }
  return context;
}
